import { tcgDataService } from './tcgDataService';
import { 
  getUserOnePieceCardOwnerships, 
  upsertOnePieceCardOwnership,
  OnePieceCardOwnership
} from './opCardOwnershipService';

export interface SealedProduct {
  id: string;
  name: string;
  type: string;
  setId: string;
  releaseDate: string;
  imageUrl: string;
  msrp: number;
}

export interface EnrichedSealedProduct extends SealedProduct {
  owned?: boolean;
  quantity?: number;
  purchasePrice?: number;
  notes?: string;
}

/**
 * Fetch all sealed products from API
 */
export async function getSealedProducts(): Promise<SealedProduct[]> {
  try {
    return await tcgDataService.getOnePieceSealedProducts();
  } catch (error) {
    console.error('Error fetching sealed products:', error);
    return [];
  }
}

/**
 * Search sealed products by name or set ID
 */
export async function searchSealedProducts(query: string): Promise<SealedProduct[]> {
  const normalizedQuery = query.toLowerCase();
  const products = await getSealedProducts();
  return products.filter(p => 
    p.name.toLowerCase().includes(normalizedQuery) || 
    p.setId.toLowerCase().includes(normalizedQuery) ||
    p.type.toLowerCase().includes(normalizedQuery)
  );
}

/**
 * Fetch enriched sealed products with ownership data
 */
export async function getEnrichedSealedProducts(): Promise<EnrichedSealedProduct[]> {
  const products = await getSealedProducts();
  
  let ownerships: OnePieceCardOwnership[] = [];
  try {
    // We fetch ownerships from the 'collection' source
    // Sealed products will have their unique IDs as 'cardId'
    ownerships = await getUserOnePieceCardOwnerships(undefined, false, 'collection');
  } catch (error) {
    console.warn('[getEnrichedSealedProducts] Failed to fetch ownerships, returning products without ownership data:', error);
  }
  
  const ownershipMap = new Map<string, OnePieceCardOwnership>();
  ownerships.forEach(o => ownershipMap.set(o.cardId, o));

  return products.map(p => {
    const ownership = ownershipMap.get(p.id);
    return {
      ...p,
      owned: !!ownership,
      quantity: ownership?.quantity || 0,
      purchasePrice: ownership?.purchasePrice,
      notes: ownership?.notes,
    };
  });
}

/**
 * Update ownership for a sealed product
 */
export async function updateSealedProductOwnership(
  productId: string, 
  quantity: number,
  setId: string,
  purchasePrice?: number,
  notes?: string
): Promise<void> {
  await upsertOnePieceCardOwnership(productId, {
    setId,
    owned: quantity > 0,
    quantity,
    purchasePrice,
    notes,
    inCollection: true
  });
}
